import { DataTypeName } from ".";

type BaseGenericPrefixes = { Array: "Multiple"; Optional: "Maybe" };

/**
 * Defines a simple grammar for generic datatypes for use in email flows that is both human-readable and machine-parseable.
 * Full type safety and intellisense are supported up to the specified recursion depth limit.
 * Probably no more than 3 levels of recursion will ever be needed.
 *
 * @remarks
 *
 * Arrays are not allowed to have optional elements, but the array itself can be optional.
 * Optional types cannot themselves be optional.
 *
 * In BNF:
 * <GenericDataType> ::= <DataTypeName> | <ArrayDataType> | <OptionalDataType>
 * <ArrayDataType> ::= "Multiple" <RequiredDataType>
 * <RequiredDataType> ::= <DataTypeName> | <ArrayDataType>
 * <OptionalDataType> ::= "Maybe" <DataTypeName> | <ArrayDataType>
 */
export type GenericPrefixes = {
  [K in keyof BaseGenericPrefixes]: `${BaseGenericPrefixes[K]} `;
};

export type GenericDataType<TDepth = 1> = TDepth extends RecursionDepths[number]
  ? DataTypeName | ArrayDataType<TDepth> | OptionalDataType<TDepth>
  : never;

type ArrayDataType<TDepth> = TDepth extends RecursionDepths[number]
  ? `${GenericPrefixes["Array"]}${RequiredDataType<Inc<TDepth>>}`
  : never;

type RequiredDataType<TDepth> = TDepth extends RecursionDepths[number]
  ? Exclude<
      GenericDataType<TDepth>,
      `${GenericPrefixes["Optional"]}${DataTypeName | ArrayDataType<TDepth>}`
    >
  : never;

type OptionalDataType<TDepth> = TDepth extends RecursionDepths[number]
  ? `${GenericPrefixes["Optional"]}${DataTypeName | ArrayDataType<TDepth>}`
  : never;

export type RecursionDepths = [1, 2, 3];
export type Inc<T> = T extends RecursionDepths[number]
  ? RecursionDepths[T]
  : never;

export type ExtractArrayType<T> =
  T extends `${GenericPrefixes["Array"]}${infer U}` ? U : never;

export type ExtractOptionalType<T> =
  T extends `${GenericPrefixes["Optional"]}${infer U}` ? U : never;

export function removeGenericFromType(type: GenericDataType) {
  return type
    .replaceAll("Multiple ", "")
    .replaceAll("Maybe ", "")
    .trim() as GenericDataType;
}
